import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Bell, BellOff, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { 
  requestNotificationPermission, 
  getNotificationSettings, 
  updateNotificationSettings,
  sendTestNotification,
  setupMessageListener,
  isNotificationSupported
} from '../services/pushNotifications';

const NotificationSettings = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [permissionStatus, setPermissionStatus] = useState('default');
  const [isSupported, setIsSupported] = useState(true);
  const [settings, setSettings] = useState({
    alerts_enabled: true,
    weather_enabled: true,
    recommendations_enabled: true
  });

  useEffect(() => {
    loadSettings();
    checkPermission();
    
    // Setup foreground message listener
    setupMessageListener((payload) => {
      toast.info(payload.notification?.title || 'New notification');
    });
  }, []);

  const checkPermission = () => {
    const supported = isNotificationSupported();
    setIsSupported(supported);
    
    // Detect iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    
    if (isIOS && !isStandalone) {
      setPermissionStatus('ios_not_installed');
      setLoading(false);
      return;
    }
    
    if (!supported) {
      setPermissionStatus('unsupported');
      setLoading(false);
      return;
    }
    
    if ('Notification' in window) {
      setPermissionStatus(Notification.permission);
    }
  };

  const loadSettings = async () => {
    try {
      const authToken = token || localStorage.getItem('token');
      const data = await getNotificationSettings(authToken);
      if (data) {
        setSettings(data);
      }
    } catch (error) {
      console.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleEnableNotifications = async () => {
    if (!isSupported) {
      toast.error('Push notifications are not supported in this browser');
      return;
    }
    
    setLoading(true);
    try {
      const authToken = token || localStorage.getItem('token');
      const fcm = await requestNotificationPermission(authToken);
      if (fcm) {
        setPermissionStatus('granted');
        toast.success('Push notifications enabled!');
      } else {
        toast.error('Could not enable notifications. Please check browser settings.');
      }
    } catch (error) {
      toast.error('Failed to enable notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleSettingChange = async (key, value) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    
    try {
      const authToken = token || localStorage.getItem('token');
      await updateNotificationSettings(newSettings, authToken);
      toast.success('Settings updated');
    } catch (error) {
      toast.error('Failed to update settings');
      setSettings(settings); // Revert
    }
  };

  const handleTestNotification = async () => {
    try {
      const authToken = token || localStorage.getItem('token');
      const result = await sendTestNotification(authToken);
      if (result) {
        toast.success(result.message);
      }
    } catch (error) {
      toast.error('Failed to send test notification');
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32 }}>
        <div className="spinner" />
      </div>
    );
  }

  const statusStyles = {
    unsupported: { background: 'rgba(255,255,255,0.04)', borderColor: 'var(--border-soft)' },
    granted: { background: 'rgba(76,175,80,0.1)', borderColor: 'rgba(76,175,80,0.3)' },
    denied: { background: 'rgba(244,67,54,0.1)', borderColor: 'rgba(244,67,54,0.3)' },
    default: { background: 'rgba(255,193,7,0.1)', borderColor: 'rgba(255,193,7,0.3)' }
  };

  const currentStatus = !isSupported ? 'unsupported' : permissionStatus;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Permission Status */}
      <div style={{
        padding: 16,
        borderRadius: 12,
        border: '1px solid',
        ...statusStyles[currentStatus]
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {permissionStatus === 'granted' ? (
              <Bell size={24} style={{ color: 'var(--success)' }} />
            ) : (
              <BellOff size={24} style={{ color: 'var(--warning)' }} />
            )}
            <div>
              <p style={{ fontWeight: 600, marginBottom: 4 }}>
                {!isSupported
                  ? 'Push notifications not available'
                  : permissionStatus === 'ios_not_installed'
                  ? 'Install app first (iOS)'
                  : permissionStatus === 'granted' 
                  ? 'Push notifications enabled' 
                  : permissionStatus === 'denied'
                  ? 'Push notifications blocked'
                  : 'Push notifications not enabled'}
              </p>
              <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                {!isSupported
                  ? 'Your browser does not support push notifications'
                  : permissionStatus === 'ios_not_installed'
                  ? 'Add SFarm to Home Screen first, then enable notifications'
                  : permissionStatus === 'granted' 
                  ? 'You will receive alerts on this device' 
                  : permissionStatus === 'denied'
                  ? 'Please enable in browser settings'
                  : 'Enable to receive field alerts and recommendations'}
              </p>
            </div>
          </div>
          
          {permissionStatus === 'ios_not_installed' && (
            <div style={{ 
              padding: 12, 
              background: 'rgba(59,130,246,0.1)', 
              border: '1px solid rgba(59,130,246,0.3)', 
              borderRadius: 10,
              fontSize: 12,
              color: 'var(--text-secondary)'
            }}>
              <strong style={{ color: 'var(--info)' }}>iOS Instructions:</strong>
              <ol style={{ margin: '8px 0 0 16px', padding: 0 }}>
                <li>Tap the Share button (box with arrow)</li>
                <li>Scroll down and tap "Add to Home Screen"</li>
                <li>Open the app from your home screen</li>
                <li>Then you can enable notifications</li>
              </ol>
            </div>
          )}
          
          {isSupported && permissionStatus !== 'granted' && permissionStatus !== 'denied' && permissionStatus !== 'ios_not_installed' && (
            <button
              onClick={handleEnableNotifications}
              className="btn-depth-primary"
              style={{ padding: '10px 18px', fontSize: 13 }}
              data-testid="enable-notifications-btn"
            >
              Allow Notifications
            </button>
          )}
        </div>
      </div>

      {/* Notification Types - only show when granted */}
      {isSupported && permissionStatus === 'granted' && (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <h3 style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-secondary)' }}>Notification Types</h3>
            
            {/* Alerts Toggle */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: 14,
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid var(--border-soft)',
              borderRadius: 12
            }}>
              <div>
                <p style={{ fontWeight: 600, marginBottom: 2 }}>Field Alerts</p>
                <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>Disease, pest, and weather alerts</p>
              </div>
              <button
                onClick={() => handleSettingChange('alerts_enabled', !settings.alerts_enabled)}
                data-testid="toggle-alerts"
                style={{
                  width: 48, height: 26, borderRadius: 13, border: 'none', cursor: 'pointer',
                  background: settings.alerts_enabled ? 'var(--success)' : 'rgba(255,255,255,0.15)',
                  transition: 'background 0.2s', position: 'relative'
                }}
              >
                <div style={{
                  width: 20, height: 20, borderRadius: '50%', background: '#fff',
                  position: 'absolute', top: 3, transition: 'left 0.2s',
                  left: settings.alerts_enabled ? 25 : 3,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                }} />
              </button>
            </div>

            {/* Weather Toggle */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: 14,
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid var(--border-soft)',
              borderRadius: 12
            }}>
              <div>
                <p style={{ fontWeight: 600, marginBottom: 2 }}>Weather Updates</p>
                <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>Severe weather warnings</p>
              </div>
              <button
                onClick={() => handleSettingChange('weather_enabled', !settings.weather_enabled)}
                data-testid="toggle-weather"
                style={{
                  width: 48, height: 26, borderRadius: 13, border: 'none', cursor: 'pointer',
                  background: settings.weather_enabled ? 'var(--success)' : 'rgba(255,255,255,0.15)',
                  transition: 'background 0.2s', position: 'relative'
                }}
              >
                <div style={{
                  width: 20, height: 20, borderRadius: '50%', background: '#fff',
                  position: 'absolute', top: 3, transition: 'left 0.2s',
                  left: settings.weather_enabled ? 25 : 3,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                }} />
              </button>
            </div>

            {/* Recommendations Toggle */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: 14,
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid var(--border-soft)',
              borderRadius: 12
            }}>
              <div>
                <p style={{ fontWeight: 600, marginBottom: 2 }}>Recommendations</p>
                <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>Spray windows and fertilizer timing</p>
              </div>
              <button
                onClick={() => handleSettingChange('recommendations_enabled', !settings.recommendations_enabled)}
                data-testid="toggle-recommendations"
                style={{
                  width: 48, height: 26, borderRadius: 13, border: 'none', cursor: 'pointer',
                  background: settings.recommendations_enabled ? 'var(--success)' : 'rgba(255,255,255,0.15)',
                  transition: 'background 0.2s', position: 'relative'
                }}
              >
                <div style={{
                  width: 20, height: 20, borderRadius: '50%', background: '#fff',
                  position: 'absolute', top: 3, transition: 'left 0.2s',
                  left: settings.recommendations_enabled ? 25 : 3,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                }} />
              </button>
            </div>
          </div>

          {/* Test Notification Button */}
          <button
            onClick={handleTestNotification}
            className="btn-depth-secondary"
            data-testid="test-notification-btn"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              padding: 14, fontSize: 14
            }}
          >
            <Send size={18} />
            Send Test Notification
          </button>
        </>
      )}
    </div>
  );
};

export default NotificationSettings;
